import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';
import { api } from '../utils/api';
import { useRefreshListener, REFRESH_EVENTS } from '../utils/refreshEvents';
import './Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.getSettings();
      setSettings(res.data);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    fetchSettings();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchSettings]);

  // Écouter les événements de rafraîchissement depuis l'admin
  useRefreshListener(REFRESH_EVENTS.SETTINGS, fetchSettings);

  const isActive = (path) => location.pathname === path;

  return (
    <motion.nav
      className={`navbar ${scrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="container">
        <div className="navbar-content">
          <Link to="/" className="navbar-brand">
            {settings?.logo ? (
              <motion.img
                src={settings.logo}
                alt={settings.siteName || 'VirtualHand'}
                className="navbar-logo-image"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              />
            ) : (
              <motion.div
                className="logo"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <span className="logo-vh">VH</span>
              </motion.div>
            )}
            <span className="brand-name">{settings?.siteName || 'VirtualHand'}</span>
          </Link>

          <button
            className="navbar-toggle"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <FiX /> : <FiMenu />}
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.ul
                className="navbar-menu mobile"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <NavLinks isActive={isActive} setIsOpen={setIsOpen} />
              </motion.ul>
            )}
          </AnimatePresence>

          <ul className="navbar-menu desktop">
            <NavLinks isActive={isActive} setIsOpen={setIsOpen} />
          </ul>
        </div>
      </div>
    </motion.nav>
  );
};

const NavLinks = ({ isActive, setIsOpen }) => (
  <>
    <li>
      <Link
        to="/"
        className={isActive('/') ? 'active' : ''}
        onClick={() => setIsOpen(false)}
      >
        Accueil
      </Link>
    </li>
    <li>
      <Link
        to="/a-propos"
        className={isActive('/a-propos') ? 'active' : ''}
        onClick={() => setIsOpen(false)}
      >
        À propos
      </Link>
    </li>
    <li>
      <Link
        to="/services"
        className={isActive('/services') ? 'active' : ''}
        onClick={() => setIsOpen(false)}
      >
        Services
      </Link>
    </li>
    <li>
      <Link
        to="/tarifs"
        className={isActive('/tarifs') ? 'active' : ''}
        onClick={() => setIsOpen(false)}
      >
        Tarifs
      </Link>
    </li>
    <li>
      <Link
        to="/contact"
        className={`nav-contact-btn ${isActive('/contact') ? 'active' : ''}`}
        onClick={() => setIsOpen(false)}
      >
        Contact
      </Link>
    </li>
  </>
);

export default Navbar;
